import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, CaretRight, CaretRight as ChevronRight, MapPin, Stethoscope, Question } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { ALL_SPECIALTIES, ALL_STATES } from "../lib/seoContent";
import { richItemListSchema, breadcrumbSchema } from "../lib/jsonLd";

const US_STATES = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
  "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
  "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
  "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
  "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming",
  "DC": "District of Columbia"
};

const POPULAR_SPECIALTIES = [
  "Cardiology",
  "Dermatology",
  "Pediatrics",
  "Psychiatry",
  "Orthopedics",
  "Family Medicine",
  "Internal Medicine",
  "Neurology"
];

const ITEMS_PER_PAGE = 20;

const StateDirectory = () => {
  const { stateCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [specialtyFilter, setSpecialtyFilter] = useState(searchParams.get("specialty") || "Internal Medicine");
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  const [entityType, setEntityType] = useState(searchParams.get("type") || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const stateName = US_STATES[stateCode.toUpperCase()] || stateCode;

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      };
      // Always include a specialty filter since NPI API requires it for state searches
      params.specialty = specialtyFilter || "Internal Medicine";
      if (cityFilter.trim()) {
        params.city = cityFilter.trim();
      }
      if (entityType && entityType !== "all") {
        params.enumeration_type = entityType;
      }
      
      const data = await api.getProvidersByState(stateCode, params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error("Error fetching state providers:", error);
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [stateCode, specialtyFilter, cityFilter, entityType, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleSpecialtyChange = (value) => {
    setSpecialtyFilter(value);
    setCurrentPage(1);
    const newParams = {};
    if (value && value !== "all") newParams.specialty = value;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (entityType && entityType !== "all") newParams.type = entityType;
    setSearchParams(newParams);
  };

  const handleCitySearch = () => {
    setCurrentPage(1);
    const newParams = {};
    if (specialtyFilter && specialtyFilter !== "all") newParams.specialty = specialtyFilter;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (entityType && entityType !== "all") newParams.type = entityType;
    setSearchParams(newParams);
  };

  const handleEntityTypeChange = (value) => {
    setEntityType(value);
    setCurrentPage(1);
    const newParams = {};
    if (specialtyFilter && specialtyFilter !== "all") newParams.specialty = specialtyFilter;
    if (cityFilter.trim()) newParams.city = cityFilter.trim();
    if (value && value !== "all") newParams.type = value;
    setSearchParams(newParams);
  };

  return (
    <>
      <Helmet>
        <title>{"NPI Lookup " + stateName + " — Find Provider NPI Numbers in " + stateCode.toUpperCase()}</title>
        <meta 
          name="description" 
          content={"NPI registry for " + stateName + " (" + stateCode.toUpperCase() + "). Search NPI numbers, find doctors, and verify " + totalCount.toLocaleString() + "+ healthcare providers in the NPPES database."}
        />
        <script type="application/ld+json">{JSON.stringify(richItemListSchema(
          `Healthcare Providers in ${stateName}`,
          `Top healthcare providers registered in ${stateName} (${stateCode.toUpperCase()}). Verified NPI numbers and NPPES credentials.`,
          results,
          `/state/${stateCode}`
        ))}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([{ name: "Home", url: "/" }, { name: "States", url: "/states" }, { name: stateName }]))}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm" data-testid="state-breadcrumb">
            <ol className="flex items-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">Healthcare Providers in {stateName}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-navy" />
              </div>
              <span className="text-sm text-slate-500 uppercase tracking-wide font-medium">
                State Directory
              </span>
            </div>
            
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="state-title">
              Healthcare Providers in {stateName}
            </h1>
            <p className="text-slate-500">
              Browse all registered healthcare providers in {stateName} ({stateCode.toUpperCase()})
            </p>
            
            {/* Filters */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Specialty</label>
                <Select value={specialtyFilter} onValueChange={handleSpecialtyChange}>
                  <SelectTrigger className="w-full sm:w-52" data-testid="state-specialty-filter">
                    <SelectValue placeholder="All specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All specialties</SelectItem>
                    {POPULAR_SPECIALTIES.map((spec) => (
                      <SelectItem key={spec} value={spec}>
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">City</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g., Miami, Chicago..."
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
                    className="w-full sm:w-52 h-9"
                    data-testid="state-city-filter"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCitySearch}
                    className="h-9 px-4"
                    data-testid="state-city-search-btn"
                  >
                    Search
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Provider Type</label>
                <Select value={entityType} onValueChange={handleEntityTypeChange}>
                  <SelectTrigger className="w-full sm:w-44 h-9" data-testid="state-entity-filter">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="NPI-1">Individual</SelectItem>
                    <SelectItem value="NPI-2">Organization</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <span className="text-sm text-slate-500 pb-1" data-testid="state-result-count">
                {loading ? "Loading..." : `Showing ${results.length} providers per page`}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar - Popular Specialties */}
            <aside className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-24">
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">
                  Popular Specialties in {stateCode.toUpperCase()}
                </h2>
                <ul className="space-y-2">
                  {POPULAR_SPECIALTIES.map((spec) => (
                    <li key={spec}>
                      <button
                        onClick={() => handleSpecialtyChange(spec)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          specialtyFilter === spec 
                            ? 'bg-teal/10 text-teal font-medium' 
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`specialty-link-${spec.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {spec}
                      </button>
                    </li>
                  ))}
                </ul>
                
                {/* Other States Link */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-medium text-slate-900 mb-3 text-sm">
                    Browse Other States
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(US_STATES).slice(0, 10).map(([code, name]) => (
                      <Link
                        key={code}
                        to={`/state/${code}`}
                        className={`px-2 py-1 text-xs rounded ${
                          code === stateCode.toUpperCase()
                            ? 'bg-navy text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {code}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Results */}
            <main className="lg:col-span-3">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                      <div className="skeleton h-4 w-20 mb-3 rounded" />
                      <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                      <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                      <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                      <div className="skeleton h-10 w-full rounded" />
                    </div>
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                  <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                    No providers found
                  </h3>
                  <p className="text-slate-500">
                    Try selecting a different specialty or check back later.
                  </p>
                </div>
              ) : (
                <>
                  <div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    data-testid="state-results-grid"
                  >
                    {results.map((provider) => (
                      <ProviderCard key={provider.number} provider={provider} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {(hasMore || currentPage > 1) && (
                    <div className="flex items-center justify-center gap-3 mt-8" data-testid="state-pagination">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                      >
                        <CaretLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      
                      <span className="text-sm text-slate-500 font-medium px-3">
                        Page {currentPage}
                      </span>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => p + 1)}
                        disabled={!hasMore}
                        className="pagination-btn"
                      >
                        Next
                        <CaretRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>

          {/* Browse Specialties in This State */}
          <div className="mt-10" data-testid="browse-specialties-in-state">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Stethoscope className="w-5 h-5 text-teal" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Browse Specialties in {stateName}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Find healthcare specialists throughout {stateName}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {ALL_SPECIALTIES.map(s => (
                  <Link
                    key={s.slug}
                    to={`/specialty/${s.slug}/${stateCode.toLowerCase()}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-teal/10 hover:text-teal text-slate-700 text-sm transition-colors"
                    data-testid={`specialty-in-state-${s.slug}`}
                  >
                    <span>{s.name}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Browse Other States */}
          <div className="mt-8" data-testid="browse-other-states">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-navy" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Browse Providers in Other States
                </h2>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mt-4">
                {ALL_STATES.map(st => (
                  <Link
                    key={st.code}
                    to={`/state/${st.code}`}
                    className={`px-3 py-2 text-sm rounded-lg text-center transition-colors ${
                      st.code === stateCode.toUpperCase()
                        ? 'bg-navy text-white'
                        : 'bg-slate-50 text-slate-600 hover:bg-teal/10 hover:text-teal'
                    }`}
                  >
                    {st.code}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* SEO Content About Healthcare in This State */}
          <div className="mt-8" data-testid="state-seo-content">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
                Healthcare Providers in {stateName}
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  {stateName} is home to thousands of registered healthcare providers serving communities across the state.
                  Whether you need a primary care physician, specialist, or mental health provider, NPI Lookup helps you
                  find verified healthcare professionals in {stateName} ({stateCode.toUpperCase()}).
                </p>
                <p>
                  Every provider listed in our directory has a valid National Provider Identifier (NPI) number registered
                  with the Centers for Medicare & Medicaid Services (CMS). This ensures you are connecting with
                  legitimate, registered healthcare professionals. Use our search tools to filter by specialty,
                  verify credentials, and find contact information for providers throughout {stateName}.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-8 mb-8" data-testid="state-faq-section">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Question className="w-5 h-5 text-navy" weight="bold" />
                <h2 className="font-display font-semibold text-xl text-slate-900">
                  Healthcare in {stateName} FAQ
                </h2>
              </div>
              <div className="space-y-4">
                {[
                  { q: `How do I find a doctor in ${stateName}?`, a: `Use NPI Lookup to search for healthcare providers in ${stateName}. You can filter by specialty, view credentials, and access contact information for registered providers. Our directory includes all providers with valid NPI numbers in ${stateCode.toUpperCase()}.` },
                  { q: `How many healthcare providers are in ${stateName}?`, a: `${stateName} has thousands of registered healthcare providers across all specialties. Use the specialty filter above to find the exact count for your area of interest. Our directory is updated regularly with data from the CMS NPI registry.` },
                  { q: `What is an NPI number and why is it important?`, a: `A National Provider Identifier (NPI) is a unique 10-digit number assigned to healthcare providers by the Centers for Medicare & Medicaid Services (CMS). It's used in healthcare transactions and helps verify that a provider is legitimately registered to practice.` },
                ].map((faq, i) => (
                  <details key={i} className="group border border-slate-200 rounded-lg">
                    <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50 transition-colors rounded-lg">
                      <span className="font-medium text-slate-900 text-sm pr-4">{faq.q}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StateDirectory;
